<template>
	<n-form-item
		:label="data.label || ''"
		:show-label="data.show_label"
		:show-feedback="hasError || !!warning || !!data.hint"
		:validation-status="hasError ? 'error' : warning ? 'warning' : undefined"
		:required="!!data.rules?.required"
		class="items-start"
		:class="{
			'col-span-6 min-[480px]:col-span-2': data.width === 2,
			'col-span-6 min-[480px]:col-span-3': data.width === 3,
			'col-span-6': data.width === 6,
			'-mt-[16px]': data.app_props?.item_styles?.includes('no-margin-top'),
			'hidden': data.element === 'select-treatment' && selectTreatmentOptions.length < 1,
		}"
	>
		<template #feedback>
			<p v-if="errors.length" class="text-xs">{{ errors[0].$message }}</p>
			<p v-else-if="warning" class="text-xs">{{ warning }}</p>
			<p v-else-if="data.hint" class="text-xs">{{ data.hint }}</p>
		</template>
		<!-- simple elements -->
		<n-input
			v-if="data.element === 'input-text'"
			:value="fieldValue"
			type="text"
			:placeholder="data.placeholder || undefined"
			:clearable="!data.rules?.required"
			@update:value="value => emit('update:fieldValue', value)"
			@blur="trimValue"
		/>
		<n-input-number
			v-else-if="data.element === 'input-number'"
			:value="convertValueToType(fieldValue, 'number')"
			:placeholder="data.placeholder || undefined"
			:min="data.rules?.min"
			:max="data.rules?.max"
			:clearable="false"
			class="w-full"
			@update:value="value => emit('update:fieldValue', value)"
		/>
		<n-input
			v-else-if="data.element === 'input-email'"
			:value="fieldValue"
			type="email"
			:placeholder="data.placeholder || undefined"
			:clearable="!data.rules?.required"
			@update:value="value => emit('update:fieldValue', value)"
			@blur="trimValue($event), checkEmail($event)"
		/>
		<n-input
			v-else-if="data.element === 'input-tel'"
			:value="fieldValue"
			type="tel"
			:placeholder="data.placeholder || undefined"
			:clearable="!data.rules?.required"
			:allow-input="value => allowInput(value, data.rules?.regex)"
			@update:value="value => emit('update:fieldValue', value)"
			@blur="trimValue"
		/>
		<n-input
			v-else-if="data.element === 'input-textarea-sm'"
			:value="fieldValue"
			type="textarea"
			:autosize="{minRows: 1}"
			:placeholder="data.placeholder || undefined"
			:clearable="!data.rules?.required"
			@update:value="value => emit('update:fieldValue', value)"
			@blur="trimValue"
		/>
		<n-input
			v-else-if="data.element === 'input-textarea-md'"
			:value="fieldValue"
			type="textarea"
			:autosize="{minRows: 4}"
			:placeholder="data.placeholder || undefined"
			:clearable="!data.rules?.required"
			@update:value="value => emit('update:fieldValue', value)"
			@blur="trimValue"
		/>
		<n-time-picker
			v-else-if="data.element === 'timepicker-eat'"
			:value="fieldValue"
			:placeholder="data.placeholder || undefined"
			:clearable="!data.rules?.required"
			format="H:mm"
			:hours="[11, 12, 13, 14, 17, 18, 19, 20, 21, 22]"
			:minutes="15"
			:actions="[]"
			placement="bottom"
			class="w-full"
			@update:value="value => emit('update:fieldValue', value)"
		/>
		<div
			v-else-if="data.element === 'datepicker-date'"
			ref="datepickerDateRef"
			class="datepicker-date w-full"
		>
			<n-date-picker
				:value="fieldValue"
				type="date"
				:first-day-of-week="0"
				format="do MMM yyyy"
				:is-date-disabled="date => date < DateTime.now().startOf('day')"
				:clearable="!data.rules?.required"
				close-on-select
				input-readonly
				:actions="null"
				:to="datepickerDateRef"
				bind-calendar-months
				placement="bottom"
				class="w-full"
				@update:value="value => emit('update:fieldValue', value)"
			/>
		</div>
		<div
			v-else-if="data.element === 'datepicker-daterange'"
			ref="datepickerDaterangeRef"
			class="datepicker-daterange w-full"
		>
			<n-date-picker
				:value="fieldValue"
				type="daterange"
				:first-day-of-week="0"
				format="do MMM yyyy"
				:is-date-disabled="date => date < DateTime.now().startOf('day')"
				:clearable="!data.rules?.required"
				close-on-select
				input-readonly
				:actions="null"
				:to="datepickerDaterangeRef"
				bind-calendar-months
				placement="bottom"
				class="w-full"
				@update:value="value => emit('update:fieldValue', value)"
			/>
		</div>
		<n-checkbox
			v-else-if="data.element === 'checkbox-privacy'"
			:checked="fieldValue"
			@update:checked="value => emit('update:fieldValue', value)"
		>
			{{ t$.privacy_1 }}
			<a
				:href="t$.privacy_url"
				target="_blank"
				class="text-blue-500 hover:underline"
				onclick="event.stopPropagation()"
			>
				{{ t$.privacy_2 }}
			</a>
			<span v-if="data.rules?.required" class="text-red-600">&nbsp;*</span>
		</n-checkbox>
		<n-checkbox
			v-else-if="data.element === 'checkbox-newsletter'"
			:checked="fieldValue"
			@update:checked="value => emit('update:fieldValue', value)"
		>
			{{ t$.newsletter_subscribe }}
			<p class="text-xs text-gray-400 italic">
				{{ t$.newsletter_spam_notice }}
			</p>
		</n-checkbox>
		<!-- complex elements -->
		<n-select
			v-else-if="data.element === 'select-treatment'"
			:value="getValueFromOptions(fieldValue, selectTreatmentOptions)"
			:placeholder="data.placeholder || t$.select"
			:options="selectTreatmentOptions"
			:clearable="!data.rules?.required"
			:disabled="selectTreatmentOptions.length === 1"
			:consistent-menu-width="false"
			@update:value="value => emit('update:fieldValue', value)"
		/>
		<n-select
			v-else-if="data.element === 'select-roomtype'"
			:value="getValueFromOptions(fieldValue, selectRoomtypeOptions)"
			:placeholder="data.placeholder || t$.select"
			:options="selectRoomtypeOptions"
			:clearable="!data.rules?.required"
			:disabled="selectRoomtypeOptions.length === 1"
			:consistent-menu-width="false"
			:render-label="selectRoomtypeRenderLabel"
			@update:value="value => emit('update:fieldValue', value)"
		/>
		<n-select
			v-else-if="data.element === 'select-minisiteevent'"
			:value="getValueFromOptions(fieldValue, selectMinisiteeventOptions)"
			:placeholder="data.placeholder || t$.select"
			:options="selectMinisiteeventOptions"
			:clearable="!data.rules?.required"
			:disabled="selectMinisiteeventOptions.length === 1"
			:render-label="selectMinisiteeventRenderLabel"
			:render-tag="selectMinisiteeventRenderTag"
			:theme-overrides="{
				peers: {
					InternalSelection: {
						heightMedium: '50px',
					},
				},
			}"
			@update:value="value => emit('update:fieldValue', value)"
		/>
		<n-select
			v-else-if="data.element === 'select-skilessontype'"
			:value="getValueFromOptions(fieldValue, selectSkilessontypeOptions)"
			:placeholder="data.placeholder || t$.select"
			:options="selectSkilessontypeOptions"
			:clearable="!data.rules?.required"
			:disabled="selectSkilessontypeOptions.length === 1"
			:consistent-menu-width="false"
			@update:value="value => emit('update:fieldValue', value)"
		/>
		<n-select
			v-else-if="data.element === 'select-skilessongrade'"
			:value="getValueFromOptions(fieldValue, selectSkilessongradeOptions)"
			:placeholder="data.placeholder || t$.select"
			:options="selectSkilessongradeOptions"
			:clearable="!data.rules?.required"
			:disabled="selectSkilessongradeOptions.length === 1"
			:consistent-menu-width="false"
			@update:value="value => emit('update:fieldValue', value)"
		/>
		<n-select
			v-else-if="data.element === 'select-skilessontechnique'"
			:value="getValueFromOptions(fieldValue, selectSkilessontechniqueOptions)"
			:placeholder="data.placeholder || t$.select"
			:options="selectSkilessontechniqueOptions"
			:clearable="!data.rules?.required"
			:disabled="selectSkilessontechniqueOptions.length === 1"
			:consistent-menu-width="false"
			@update:value="value => emit('update:fieldValue', value)"
		/>
		<!-- unknown element -->
		<p v-else class="italic text-red-500 font-bold">Field not supported: {{ data.element }}</p>
	</n-form-item>
</template>

<script setup>
	import {
		NAvatar,
		NEllipsis,
		NFormItem,
		NInput,
		NInputNumber,
		NDatePicker,
		NTimePicker,
		NCheckbox,
		NSelect,
	} from 'naive-ui'
	import {h, inject, ref, computed, toRefs} from 'vue'
	import {getFieldData} from './fields-data'
	import {parseEventDate, parseEventTitle} from './utils'
	import {getTranslations} from './translations'
	import {DateTime} from 'luxon'

	const props = defineProps({
		data: {
			type: Object,
			required: true,
		},
		fieldValue: {
			type: [Array, String, Boolean, Number, null],
			required: true,
		},
		language: {
			type: Object,
			required: true,
		},
		errors: {
			type: Array,
			required: true,
		},
	})
	const {data, fieldValue, language, errors} = toRefs(props)
	const minisiteDate = inject('minisiteData')
	const t$ = inject('t$')
	const t$it = getTranslations(1)
	const emit = defineEmits(['update:fieldValue'])

	const hasError = computed(() => errors.value.length > 0)
	const warning = ref('')

	function trimValue(event) {
		emit('update:fieldValue', event.target.value.trim())
	}

	function allowInput(value, regex) {
		if (!regex || !value) {
			return true
		}

		return new RegExp(regex).test(value)
	}

	function getValueFromOptions(value, options) {
		if (options.length === 0) {
			emit('update:fieldValue', null)
			return null
		}

		if (options.length === 1) {
			emit('update:fieldValue', options[0].value)
			return options[0].value
		}

		const result = options.find(x => x.value === value)?.value ?? null

		if (result !== value) {
			emit('update:fieldValue', result)
		}

		return result
	}

	function convertValueToType(value, type) {
		if (typeof value === type) {
			return value
		}

		if (type === 'number') {
			return parseInt(value) || null
		}

		throw new Error('Unknown type in convertValueToType', type)
	}

	// elements stuff
	const datepickerDaterangeRef = ref()
	const datepickerDateRef = ref()

	// input-email
	async function checkEmail(event) {
		const value = event.target.value?.trim()

		if (!value) {
			return
		}

		const req = await fetch(`https://api.webcloud.it/validators/email/${value}`)
		const res = await req.json()

		warning.value = !res.address ? t$.mail_address_warning : ''
	}

	// select-treatment
	const selectTreatmentOptions = ref([])
	if (data.value.element === 'select-treatment') {
		const fieldData = await getFieldData(data.value.element, language.value, {
			minisiteId: minisiteDate.id,
		})

		selectTreatmentOptions.value = fieldData.data.map(x => ({
			label: x.name,
			value: x.key,
		}))
	}

	// select-roomtype
	const selectRoomtypeOptions = ref([])
	if (data.value.element === 'select-roomtype') {
		const fieldData = await getFieldData(data.value.element, language.value, {
			minisiteId: minisiteDate.id,
		})

		selectRoomtypeOptions.value = fieldData.data.map(x => ({
			...x,
			label: x.name,
			value: x.key,
		}))
	}

	function selectRoomtypeRenderLabel(option) {
		if (option.photo && !/_thumb\.[a-z]{3,4}$/.test(option.photo)) {
			const ext = option.photo.split('.').pop()
			option.photo = option.photo.replace(`.${ext}`, `_thumb.${ext}`)
		}

		return h(
			'div',
			{
				class: 'flex items-center gap-2',
			},
			[
				option.photo &&
					h(NAvatar, {
						src: `${minisiteDate.imagesPath}${option.photo}`,
						round: false,
						size: 28,
						objectFit: 'cover',
						imgProps: {
							alt: option.photo_alt,
						},
						class: 'flex-none',
					}),
				h(NEllipsis, {}, {default: () => option.name}),
			]
		)
	}

	// select-minisiteevent
	const selectMinisiteeventOptions = ref([])
	if (data.value.element === 'select-minisiteevent') {
		const fieldData = await getFieldData(data.value.element, language.value, {
			minisiteId: minisiteDate.id,
		})

		selectMinisiteeventOptions.value = fieldData.data.map(x => ({
			...x,
			label: x.title,
			value: x.id,
		}))
	}

	function selectMinisiteeventRenderLabel(option) {
		return selectMinisiteeventRender(option, false)
	}

	function selectMinisiteeventRenderTag({option}) {
		return selectMinisiteeventRender(option, true)
	}

	function selectMinisiteeventRender(option, compact) {
		return h('div', {class: 'flex items-center gap-2 py-2'}, [
			h(NAvatar, {
				size: compact ? 36 : 48,
				src: option.images.url,
				objectFit: 'cover',
				imgProps: {
					alt: option.images.alt,
				},
				class: 'flex-none',
			}),
			h(
				'div',
				{class: 'flex flex-col min-w-0'},
				compact
					? [
							h(NEllipsis, {class: 'text-xs opacity-75', tooltip: false}, () =>
								parseEventDate(option.date, language.value)
							),
							h(NEllipsis, {class: 'font-medium', tooltip: false}, () =>
								parseEventTitle(option.title)
							),
					  ]
					: [
							h(
								'span',
								{class: 'text-xs opacity-75 whitespace-normal'},
								parseEventDate(option.date, language.value)
							),
							h('span', {class: 'font-medium whitespace-normal'}, parseEventTitle(option.title)),
					  ]
			),
		])
	}

	// select-skilessontype
	const selectSkilessontypeOptions = computed(() => {
		return [
			{
				label: t$.skilessontype_1,
				value: t$it.skilessontype_1,
			},
			{
				label: t$.skilessontype_2,
				value: t$it.skilessontype_2,
			},
		]
	})

	// select-skilessongrade
	const selectSkilessongradeOptions = computed(() => {
		return [
			{
				label: t$.skilessongrade_1,
				value: t$it.skilessongrade_1,
			},
			{
				label: t$.skilessongrade_2,
				value: t$it.skilessongrade_2,
			},
			{
				label: t$.skilessongrade_3,
				value: t$it.skilessongrade_3,
			},
			{
				label: t$.skilessongrade_4,
				value: t$it.skilessongrade_4,
			},
			{
				label: t$.skilessongrade_5,
				value: t$it.skilessongrade_5,
			},
		]
	})

	// select-skilessontechnique
	const selectSkilessontechniqueOptions = computed(() => {
		return [
			{
				label: t$.skilessontechnique_1,
				value: t$it.skilessontechnique_1,
			},
			{
				label: t$.skilessontechnique_2,
				value: t$it.skilessontechnique_2,
			},
			{
				label: t$.skilessontechnique_3,
				value: t$it.skilessontechnique_3,
			},
			{
				label: t$.skilessontechnique_4,
				value: t$it.skilessontechnique_4,
			},
		]
	})
</script>

<style lang="scss" scoped>
	.datepicker-daterange,
	.datepicker-date {
		:deep(.n-date-panel .n-date-panel-calendar--end) {
			@apply hidden min-[480px]:block;
		}
	}

	:deep(.n-date-panel .n-date-panel-month .n-date-panel-month__fast-prev),
	:deep(.n-date-panel .n-date-panel-month .n-date-panel-month__fast-next) {
		@apply hidden;
	}

	:deep(.n-date-panel .n-date-panel-month .n-date-panel-month__prev),
	:deep(.n-date-panel .n-date-panel-month .n-date-panel-month__next) {
		@apply col-span-2;
	}
</style>
